import { useState } from "react";
import { ArrowRight, Trash2 } from "lucide-react";
import { useResolutionMutations, useResolutions } from "../api/hooks";
import type { Resolution } from "../api/types";
import { MONTHS_SHORT } from "../lib/date";
import { useUi } from "../state/ui";
import { DoneSection, PageHead, RowSection } from "./TasksPage";
import "./pages.css";

/** "desde 1 de out." from an ISO instant (local calendar date is close enough here). */
function since(iso: string): string {
  const d = new Date(iso);
  return `desde ${d.getDate()} de ${MONTHS_SHORT[d.getMonth()]}`;
}

export function ResolutionRow({ r }: { r: Resolution }) {
  const ui = useUi();
  const { create, update, remove } = useResolutionMutations();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(r.title);
  const saving = r.id.startsWith("tmp-");
  const fail = (err: unknown) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`);

  const toggle = () =>
    update.mutate(
      { r, completed: !r.completed },
      {
        onSuccess: () => !r.completed && ui.showToast("Resolução concluída", { label: "Desfazer", run: () => update.mutate({ r: { ...r, completed: true }, completed: false }) }),
        onError: fail
      }
    );

  const rename = () => {
    setEditing(false);
    const t = title.trim();
    if (!t) setTitle(r.title);
    else if (t !== r.title) update.mutate({ r, title: t }, { onError: fail });
  };

  const removeIt = () =>
    remove.mutate(r, {
      onSuccess: () =>
        ui.showToast("Resolução excluída", {
          label: "Desfazer",
          run: () => create.mutate(r.title, { onSuccess: (n) => r.completed && update.mutate({ r: n, completed: true }) })
        }),
      onError: fail
    });

  return (
    <li className={`row${r.completed ? " done" : ""}`}>
      <label className="checkbox round row-check">
        <input type="checkbox" checked={r.completed} disabled={saving} onChange={toggle} aria-label={r.completed ? `Reabrir ${r.title}` : `Concluir ${r.title}`} />
      </label>
      {editing ? (
        <form
          className="row-main"
          onSubmit={(e) => {
            e.preventDefault();
            rename();
          }}
        >
          <input
            className="field row-edit"
            autoFocus
            value={title}
            maxLength={300}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={rename}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setTitle(r.title);
                setEditing(false);
              }
            }}
            aria-label="Título da resolução"
          />
        </form>
      ) : (
        <button className="row-main" disabled={saving} onClick={() => setEditing(true)} title="Clique para editar">
          <span className="row-title">{r.title}</span>
        </button>
      )}
      <span className="row-meta">
        <span className="row-when">{since(r.createdAt)}</span>
        <button className="icon-btn sm row-delete" aria-label={`Excluir ${r.title}`} disabled={saving} onClick={removeIt}>
          <Trash2 size={15} />
        </button>
      </span>
    </li>
  );
}

export function ResolutionQuickAdd() {
  const ui = useUi();
  const { create } = useResolutionMutations();
  const [title, setTitle] = useState("");
  return (
    <form
      className="add-line"
      onSubmit={(e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) return;
        create.mutate(t, { onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`) });
        setTitle("");
      }}
    >
      <input className="add-input" value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} placeholder="Algo para resolver um dia…" aria-label="Nova resolução" />
      <button type="submit" className="btn primary add-submit" disabled={!title.trim()} aria-label="Adicionar resolução">
        <ArrowRight size={16} />
      </button>
    </form>
  );
}

/** "Resoluções": things to get done some day, with no date. */
export function ResolutionsPage() {
  const { data, isLoading } = useResolutions();
  const pending = data?.pending ?? [];
  const done = data?.completed ?? [];

  return (
    <div className="page-scroll">
      <div className="page">
        <PageHead label="Resoluções" title="Sem data, sem pressa.">
          Pendências para resolver quando der. Marque quando estiver feita.
        </PageHead>
        <ResolutionQuickAdd />
        {isLoading ? (
          <div className="center-fill" style={{ height: 200 }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            {!pending.length ? (
              <p className="empty">Nenhuma resolução em aberto.</p>
            ) : (
              <RowSection title="Em aberto" count={pending.length}>
                {pending.map((r) => <ResolutionRow key={r.id} r={r} />)}
              </RowSection>
            )}
            <DoneSection title="Concluídas · 30 dias" count={done.length}>
              {done.map((r) => <ResolutionRow key={r.id} r={r} />)}
            </DoneSection>
          </>
        )}
      </div>
    </div>
  );
}
